import React, { useState, useMemo } from 'react'
import styled from '@emotion/styled'
import { isValidNumber, getInternationalPhoneNumber } from '../../../../pure-js/libs/PhoneHelper'
import { getGrooverText } from '../../libs/GrooverTextRepository'
import useAppState from '../../hooks/useAppState'
import GrooverText from '../GrooverText'
import { PhoneInputWithFlag } from '../PhoneInputWithFlag'

interface PhoneInputFormProps {
  onSubmit: (phoneNumber: string) => Promise<void>
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 400px;
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #000000;
  margin: 0;
  text-align: center;
`

const Description = styled.p`
  font-size: 16px;
  color: #666666;
  margin: 0;
  text-align: center;
`

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const SubmitButton = styled.button`
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  background: var(--portal-primary, #7c3aed);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: var(--portal-primary-dark, #6d28d9);
  }

  &:disabled {
    background: #4a4a4a;
    cursor: not-allowed;
  }
`

const ErrorText = styled.p`
  font-size: 14px;
  color: #ef4444;
  margin: 0;
`

const DEFAULT_PHONE_COUNTRY = 'SE'

export const PhoneInputForm: React.FC<PhoneInputFormProps> = ({ onSubmit }) => {
  const [phoneNumber, setPhoneNumber] = useState('')
  const [selectedPhoneCountry, setSelectedPhoneCountry] = useState<string>(DEFAULT_PHONE_COUNTRY)
  const [error, setError] = useState<string>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { state } = useAppState()
  const selectedLanguage = state.selectedLanguage

  // Validate phone number in real-time using selected country from dropdown
  const isPhoneValid = useMemo(() => {
    if (!phoneNumber.trim()) return false
    return isValidNumber(phoneNumber, selectedPhoneCountry as any)
  }, [phoneNumber, selectedPhoneCountry])

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setPhoneNumber(value)

    // Clear error when user starts typing
    if (error) {
      setError(undefined)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate phone number using existing helper
    if (!isPhoneValid) {
      setError(getGrooverText('validationPhoneInvalid', selectedLanguage))
      return
    }

    setIsSubmitting(true)
    setError(undefined)

    try {
      // Convert to international format (E.164) before sending to Firebase
      const internationalPhone = getInternationalPhoneNumber(phoneNumber, selectedPhoneCountry as any)
      await onSubmit(internationalPhone)
    } catch {
      setError(getGrooverText('portalLoginSmsError', selectedLanguage))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Title>
        <GrooverText textKey="portalLoginTitle" />
      </Title>
      <Description>
        <GrooverText textKey="portalLoginDescription" />
      </Description>

      <InputWrapper>
        <PhoneInputWithFlag
          name="phoneNumber"
          value={phoneNumber}
          onChange={handlePhoneChange}
          onCountryChange={setSelectedPhoneCountry}
          placeholder="70 123 45 67"
          defaultCountry={DEFAULT_PHONE_COUNTRY}
          disabled={isSubmitting}
          fullWidth
          fieldState={error ? { error: { message: error } } : undefined}
        />
        {error && <ErrorText>{error}</ErrorText>}
      </InputWrapper>

      <SubmitButton type="submit" disabled={isSubmitting || !isPhoneValid}>
        {isSubmitting ? <GrooverText textKey="portalLoginSending" /> : <GrooverText textKey="portalLoginSendCode" />}
      </SubmitButton>
    </Form>
  )
}
